import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { ThemeProvider } from "@agronod/mui-components";
import { adminThemeOptions } from "@/theme";

// Layout components
import MainLayout from "@/components/layout/MainLayout";
import AdminLayout from "@/components/layout/AdminLayout";

// Route protection
import ProtectedRoute from "./ProtectedRoute";
import ProtectedAdminRoute from "./ProtectedAdminRoute";

// Constants
import { ROUTES } from "@/constants";
import { UserRole } from "@/types";

// Lazy load pages for better performance
const CallbackPage = React.lazy(() => import("@/pages/auth/CallbackPage"));
const SilentCallbackPage = React.lazy(
  () => import("@/pages/auth/SilentCallbackPage")
);
const DashboardPage = React.lazy(() => import("@/pages/DashboardPage"));
const B2bHomePage = React.lazy(() => import("@/pages/b2b/B2bHomePage"));
const DocumentsPage = React.lazy(() => import("@/pages/DocumentsPage"));
// Temporarily import ControlsPage directly to fix dynamic import issue
import ControlsPage from "@/pages/ControlsPage";
const CreateControlPage = React.lazy(() => import("@/pages/CreateControlPage"));
const EditControlPage = React.lazy(() => import("@/pages/EditControlPage"));
const ControlDetailPage = React.lazy(() => import("@/pages/ControlDetailPage"));
const SharesPage = React.lazy(() => import("@/pages/SharesPage"));
const PublicSharePage = React.lazy(() => import("@/pages/PublicSharePage"));
const ProfilePage = React.lazy(() => import("@/pages/ProfilePage"));
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"));
const UnauthorizedPage = React.lazy(() => import("@/pages/UnauthorizedPage"));

// Admin pages
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminCreateUserPage from "@/pages/admin/AdminCreateUserPage";
import AdminEditUserPage from "@/pages/admin/AdminEditUserPage";
import AdminControlTypesPage from "@/pages/admin/AdminControlTypesPage";
import AdminIssuersPage from "@/pages/admin/AdminIssuersPage";
import AdminTagsPage from "@/pages/admin/AdminTagsPage";
import AdminSystemHealthPage from "@/pages/admin/AdminSystemHealthPage";
import AdminAuditLogsPage from "@/pages/admin/AdminAuditLogsPage";

// Loading component for suspense fallback
const PageLoading: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "50vh"
    }}>
    <CircularProgress size={48} />
  </Box>
);

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Redirect home to dashboard */}
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <Navigate to={ROUTES.DASHBOARD} replace />
            </ProtectedRoute>
          }
        />

        {/* Protected routes (require authentication) */}
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.DOCUMENTS}
          element={
            <ProtectedRoute>
              <MainLayout>
                <DocumentsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CONTROLS}
          element={
            <ProtectedRoute>
              <MainLayout>
                <ControlsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.CREATE_CONTROL}
          element={
            <ProtectedRoute>
              <MainLayout>
                <CreateControlPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/controls/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ControlDetailPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/controls/:id/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EditControlPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.SHARES}
          element={
            <ProtectedRoute>
              <MainLayout>
                <SharesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Public share route (no authentication required). Recipient-facing,
            so it runs the Admin theme: burgundy where the farmer sees yellow.
            The design-system ThemeProvider renders its own CssBaseline, so this
            page gets a second one; harmless, both themes share the same base. */}
        <Route
          path={`${ROUTES.PUBLIC_SHARE_PREFIX}:token`}
          element={
            <ThemeProvider options={adminThemeOptions}>
              <PublicSharePage />
            </ThemeProvider>
          }
        />

        {/* OIDC callback routes */}
        <Route path="/authentication/callback" element={<CallbackPage />} />

        <Route
          path="/authentication/silent-callback"
          element={<SilentCallbackPage />}
        />

        <Route path="/b2b/authentication/callback" element={<CallbackPage />} />

        <Route
          path="/b2b/authentication/silent-callback"
          element={<SilentCallbackPage />}
        />

        {/* B2B routes (require AGROKOLL_B2B role) */}
        <Route
          path="/b2b"
          element={
            <ProtectedAdminRoute>
              <B2bHomePage />
            </ProtectedAdminRoute>
          }
        />

        {/* Admin routes (require admin permissions) */}
        <Route
          path={ROUTES.ADMIN}
          element={
            <ProtectedAdminRoute>
              <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN_DASHBOARD}
          element={
            <ProtectedAdminRoute>
              <AdminLayout>
                <AdminDashboardPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN_USERS}
          element={
            <ProtectedAdminRoute requiredRole={UserRole.AGROKOLL_SYSTEM_ADMIN}>
              <AdminLayout>
                <AdminUsersPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/users/new"
          element={
            <ProtectedAdminRoute requiredRole={UserRole.AGROKOLL_SYSTEM_ADMIN}>
              <AdminLayout>
                <AdminCreateUserPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path="/admin/users/:id/edit"
          element={
            <ProtectedAdminRoute requiredRole={UserRole.AGROKOLL_SYSTEM_ADMIN}>
              <AdminLayout>
                <AdminEditUserPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN_CONTROL_TYPES}
          element={
            <ProtectedAdminRoute>
              <AdminLayout>
                <AdminControlTypesPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN_ISSUERS}
          element={
            <ProtectedAdminRoute>
              <AdminLayout>
                <AdminIssuersPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN_TAGS}
          element={
            <ProtectedAdminRoute requiredRole={UserRole.AGROKOLL_SYSTEM_ADMIN}>
              <AdminLayout>
                <AdminTagsPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        {/* The vocabulary page lived here while it managed categories (pre AG-249). */}
        <Route
          path="/admin/document-categories"
          element={<Navigate to={ROUTES.ADMIN_TAGS} replace />}
        />

        <Route
          path={ROUTES.ADMIN_SYSTEM_HEALTH}
          element={
            <ProtectedAdminRoute requiredRole={UserRole.AGROKOLL_SYSTEM_ADMIN}>
              <AdminLayout>
                <AdminSystemHealthPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        <Route
          path={ROUTES.ADMIN_AUDIT_LOGS}
          element={
            <ProtectedAdminRoute requiredRole={UserRole.AGROKOLL_SYSTEM_ADMIN}>
              <AdminLayout>
                <AdminAuditLogsPage />
              </AdminLayout>
            </ProtectedAdminRoute>
          }
        />

        {/* Redirect /app to dashboard for authenticated users */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Navigate to={ROUTES.DASHBOARD} replace />
            </ProtectedRoute>
          }
        />

        {/* Unauthorized page (no permission) */}
        <Route path={ROUTES.UNAUTHORIZED} element={<UnauthorizedPage />} />

        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
